import { HttpClient } from '@zeta-core';
import {
  APIResponse,
  ICreditBalanceResponse,
  ICreditHistoryResponse,
  ICreditsPricingResponse,
  ICreditsPurchaseCallbackRequest,
  ICreditsPurchaseRequest,
  ICreditsPurchaseResponse,
} from '@zeta-types';
import { CREDITS_SERVICE } from '../services-path';

export interface CreditsRequestContext {
  advisorId?: string;
  advisorFirmId?: string;
}

const createIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `credits-${crypto.randomUUID()}`;
  }
  return `credits-${Date.now()}`;
};

export const CreditsService = () => {
  const requestConfig = (context: CreditsRequestContext = {}) => ({
    headers: {
      ...(context.advisorId ? { 'X-Advisor-Id': context.advisorId } : {}),
      ...(context.advisorFirmId ? { 'X-Advisor-Firm-Id': context.advisorFirmId } : {}),
    },
  });

  return {
    getPricing: (context: CreditsRequestContext = {}): APIResponse<ICreditsPricingResponse> =>
      HttpClient.get(`${CREDITS_SERVICE}/config/pricing`, requestConfig(context)),

    purchaseWallet: (payload: ICreditsPurchaseRequest, context: CreditsRequestContext = {}): APIResponse<ICreditsPurchaseResponse> =>
      HttpClient.post(`${CREDITS_SERVICE}/wallet/purchase`, payload, {
        ...requestConfig(context),
        headers: {
          ...(context.advisorId ? { 'X-Advisor-Id': context.advisorId } : {}),
          ...(context.advisorFirmId ? { 'X-Advisor-Firm-Id': context.advisorFirmId } : {}),
          'Idempotency-Key': createIdempotencyKey(),
        },
      }),

    completePurchaseCallback: (payload: ICreditsPurchaseCallbackRequest, context: CreditsRequestContext = {}): APIResponse<ICreditsPurchaseResponse> =>
      HttpClient.post(`${CREDITS_SERVICE}/wallet/purchase/callback`, payload, requestConfig(context)),

    getCreditBalance: (context: CreditsRequestContext = {}): APIResponse<ICreditBalanceResponse> =>
      HttpClient.get(`${CREDITS_SERVICE}/wallet`, requestConfig(context)).then((response) => ({
        ...response,
        data: {
          totalAmount: Number(response.data.balance || 0),
          availableAmount: Number(response.data.availableBalance || 0),
        },
      })),

    getCreditHistory: (context: CreditsRequestContext = {}): APIResponse<ICreditHistoryResponse[]> =>
      HttpClient.get(`${CREDITS_SERVICE}/wallet/transactions`, requestConfig(context)).then((response) => ({
        ...response,
        data: (response.data || []).map((transaction: any) => ({
          advisorId: '',
          subscriptionId: transaction.transactionId,
          invoiceId: '',
          transactionType: transaction.transactionType,
          transactionDesc: transaction.description,
          transactionDate: transaction.createdAt,
          status: 'COMPLETED',
          creditReferenceType: transaction.reference,
          amount: Number(transaction.amount || 0),
          createdAt: transaction.createdAt,
          createdBy: '',
        })),
      })),
  };
};
